import { useParams, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import IntroSection from './components/IntroSection';
import ReviewSection from './components/ReviewSection';
import TeacherSection from './components/TeacherSection';
import { classImageList } from '../../constants/classImages';

const ClassDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'intro' | 'teacher' | 'reviews'>(
    'intro'
  );
  const API_URL = import.meta.env.VITE_API_URL;
  const { data, isLoading } = useQuery({
    queryKey: ['class', id],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/bbs/ajax.get_class.php?cl_id=${id}`);
      return res.json();
    }
  });
  const item = data?.item
    ? { ...data.item, file: classImageList[data.item.name?.trim()] || null }
    : null;

  if (isLoading || !item)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-400">로딩중...</div>
      </div>
    );

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 text-gray-700 hover:text-purple-600 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line text-xl"></i>
              <span className="font-bold text-xl">AIKnow 라이브 클래스</span>
            </button>
            <div className="flex items-center space-x-4">
              <button className="text-gray-600 hover:text-purple-600 transition-colors cursor-pointer">
                <i className="ri-heart-line text-2xl"></i>
              </button>
              <button className="text-gray-600 hover:text-purple-600 transition-colors cursor-pointer">
                <i className="ri-share-line text-2xl"></i>
              </button>
            </div>
          </div>
        </div>
      </nav>
      <div className="relative h-96 bg-gradient-to-br from-purple-100 to-blue-100">
        <img
          src={item?.file}
          alt={item?.name}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
      </div>
      <div className="max-w-7xl mx-auto px-4 -mt-20 relative z-10">
        <div className="bg-white rounded-3xl shadow-2xl p-8 mb-8">
          <div className="flex flex-wrap gap-2 mb-4">
            {item?.categories?.map((category: string, index: number) => (
              <span
                key={index}
                className="bg-blue-100 text-blue-600 px-3 py-1 rounded-full text-sm font-medium"
              >
                {category}
              </span>
            ))}
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {item?.name}
          </h1>
          <p className="text-xl text-gray-600 mb-6">{item?.course}</p>
          <div className="flex items-center space-x-6 mb-6">
            <div className="flex items-center space-x-2 text-gray-600">
              <i className="ri-group-line"></i>
              <span>{item?.capacity}명 수강</span>
            </div>
          </div>
          <div className="border-t border-gray-200 pt-6 mb-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div>
                <div className="text-sm text-gray-500 mb-1">수업 유형</div>
                <div className="text-lg font-bold text-gray-900">라이브</div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">권장 연령</div>
                <div className="text-lg font-bold text-gray-900">
                  {item?.age}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">수업 인원</div>
                <div className="text-lg font-bold text-gray-900">
                  {item?.capacity}명
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">수업 시간</div>
                <div className="text-lg font-bold text-gray-900">
                  {item?.time}분
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between bg-purple-50 rounded-2xl p-6">
            <div>
              {item.payLink && (
                <>
                  <div className="text-sm text-gray-600 mb-1">1회 수업료</div>
                  <div className="text-3xl font-bold text-purple-600">
                    {item?.salePrice.toLocaleString()}원
                    {item.originalPrice != item.salePrice && (
                      <span className="text-sm text-red-500 line-through ml-2">
                        {Number(item.originalPrice).toLocaleString()}원
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
            <button 
              onClick={() => {
                if(item.payLink) {
                  window.open(item.payLink, '_blank', 'noopener,noreferrer')
                } else {
                  alert("준비중입니다.");
                }
              }}
              className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-8 py-4 rounded-full text-lg font-bold hover:from-blue-600 hover:to-purple-700 transition-all transform hover:scale-105 whitespace-nowrap cursor-pointer">
              신청하기
            </button>
          </div>
        </div>
        <div className="bg-white rounded-3xl shadow-lg mb-8">
          <div className="border-b border-gray-200">
            <div className="flex space-x-8 px-8">
              <button
                onClick={() => setActiveTab('intro')}
                className={`py-4 px-2 font-bold border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
                  activeTab === 'intro'
                    ? 'border-purple-600 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                수업 소개
              </button>
              <button
                onClick={() => setActiveTab('teacher')}
                className={`py-4 px-2 font-bold border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
                  activeTab === 'teacher'
                    ? 'border-purple-600 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                선생님 소개
              </button>
              <button
                onClick={() => setActiveTab('reviews')}
                className={`py-4 px-2 font-bold border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
                  activeTab === 'reviews'
                    ? 'border-purple-600 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                후기 (98)
              </button>
            </div>
          </div>
          <div className="p-8">
            {/* 수업 소개 탭 */}
            {activeTab === 'intro' && id && <IntroSection id={id} />}

            {/* 선생님 소개 탭 */}
            {activeTab === 'teacher' && id && <TeacherSection id={id} />}

            {/* 후기 탭 */}
            {activeTab === 'reviews' && id && <ReviewSection id={id} />}
          </div>
        </div>
      </div>
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-600">1회 수업료</div>
              <div className="text-2xl font-bold text-purple-600">
                {item?.salePrice.toLocaleString()}원
              </div>
            </div>
            <button className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-8 py-4 rounded-full text-lg font-bold hover:from-blue-600 hover:to-purple-700 transition-all transform hover:scale-105 whitespace-nowrap cursor-pointer">
              신청하기
            </button>
          </div>
        </div>
      </div>
      <div className="h-24"></div>
    </div>
  );
};

export default ClassDetail;
